import { FC, ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import FormRow from '../../../components/FormRow';
import { colors, fontWeights } from '../../../theme/tokens';
import CopyButton from '../../Archive/CopyButton';
import { DraftDocument } from '../../Archive/types';
import { formatDate } from '../../Home/useDocumentResolvers';

// The read-only record of /document/:id (no design in the XD, gap 3):
// the same label / value rows of the "Valider les infos" summary
// (screen 5), over the white card, with what screen 5 does not have
// because it does not yet exist at archiving time — Date d'archivage, Archiveur — and the
// page count (ROADMAP.md, Part 3, "Outside the wizard").
//
// A document without pages (archived through the previous path) says so in the
// "Pages" row instead of faking a count.

type Props = {
  document: DraftDocument;
  serviceName: string;
  issuerName: string;
  archiverName: string;
  pageCount: number;
  isLegacy: boolean;
};

const Value: FC<{ children: ReactNode; testId?: string }> = ({ children, testId }) => (
  <Typography
    data-testid={testId}
    sx={{
      fontWeight: fontWeights.medium,
      color: colors.textBase,
      minWidth: 0,
      overflowWrap: 'anywhere',
      py: 0.5,
    }}
  >
    {children}
  </Typography>
);

const DocumentSheet: FC<Props> = ({
  document,
  serviceName,
  issuerName,
  archiverName,
  pageCount,
  isLegacy,
}) => {
  const url = document.verification_url?.trim() ?? '';

  return (
    <Box
      data-testid="document-sheet"
      sx={{ m: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}
    >
      <FormRow label="Date d'émission" tone="light">
        <Value testId="sheet-issuing-date">
          {document.issuing_date ? formatDate(document.issuing_date) : '—'}
        </Value>
      </FormRow>
      <FormRow label="Date d'archivage" tone="light">
        <Value testId="sheet-archiving-date">
          {document.archiving_date ? formatDate(document.archiving_date) : '—'}
        </Value>
      </FormRow>
      <FormRow label="Service" tone="light">
        <Value testId="sheet-service">{serviceName}</Value>
      </FormRow>
      <FormRow label="Émetteur" tone="light">
        <Value testId="sheet-issuer">{issuerName}</Value>
      </FormRow>
      <FormRow label="Archiveur" tone="light">
        <Value testId="sheet-archiver">{archiverName}</Value>
      </FormRow>
      <FormRow label="URL de vérification" tone="light">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <Value testId="sheet-verification-url">{url || '—'}</Value>
          {url && (
            <CopyButton
              value={url}
              label="Copier l'URL de vérification"
              color={colors.accent}
            />
          )}
        </Box>
      </FormRow>
      <FormRow label="Pages" tone="light">
        <Value testId="sheet-pages">
          {isLegacy ? 'Fichier unique (archivé sans pages)' : pageCount}
        </Value>
      </FormRow>
    </Box>
  );
};

export default DocumentSheet;
